import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import DatePickerComponent from '../common/DatePickerComponent';
import { getPackingLabels } from '../../store/actions/picker.action';
import { commaSeparatedStringToArray } from '../../config/utils';
import {
    getOrderByLabelService,
    getOrderItemsService,
    completePackVerificationService,
} from '../../services/packVerification.service';

/**
 * MobilePackPlan
 * -----------------------------------------------------------------------------
 * Mobile-responsive Packer "Validate" view.
 *
 * Workflow:
 *   1. Packer scans an Order ID OR Tracking No. into a single input — the
 *      backend (`/api/PackVerification/GetOrderByLabel`) auto-detects which
 *      identifier was scanned. No toggle is exposed in the UI.
 *   2. The order is fetched along with its expected items and pushed into a
 *      local `orders` list as a card. The checklist expands inline (no modal).
 *   3. Packer taps a circle next to each item to mark it as visually verified.
 *      When every item on a card is ticked the status badge flips to
 *      "Validation complete" and the "Seal & Print" button becomes active.
 *   4. Tapping "Seal & Print" before all items are ticked surfaces a warning
 *      toast. Once complete, the order is sealed via the existing complete
 *      endpoint and moved into the "Packed" tab.
 *
 * State is kept local — the redux `packVerificationReducer` already exists but
 * only stores ONE active order, which conflicts with the multi-order card list
 * required here. Direct service calls (matching `PickList/index.js` `processScan`)
 * keep this component decoupled from desktop flows.
 */

const STATUS = {
    WAITING: 'waiting',          // freshly added, items still loading
    VALIDATING: 'validating',    // items loaded, partial / no checks
    COMPLETE: 'complete',        // every item checked, ready to seal
    PACKED: 'packed',            // sealed via Seal & Print
};

const StatusBadge = ({ status }) => {
    if (status === STATUS.COMPLETE) {
        return (
            <span className="mpp-pack-status mpp-pack-status--complete">
                <i className="fa fa-check" aria-hidden="true"></i> Validation complete
            </span>
        );
    }
    if (status === STATUS.VALIDATING || status === STATUS.WAITING) {
        return (
            <span className="mpp-pack-status mpp-pack-status--validating">
                <i className="fa fa-hourglass-half" aria-hidden="true"></i>{' '}
                {status === STATUS.WAITING ? 'Waiting to scan' : 'Validating order'}
            </span>
        );
    }
    return null;
};

StatusBadge.propTypes = { status: PropTypes.string.isRequired };

const PackOrderCard = ({ order, onToggleExpand, onToggleItem, onSeal, sealingId }) => {
    const total = order.items.length;
    const checked = order.items.filter((i) => i.isChecked).length;
    const allChecked = total > 0 && checked === total;
    const isSealing = sealingId === order.id;

    return (
        <div className="mpp-pack-card">
            <div className="mpp-pack-card-head">
                <div className="mpp-pack-card-titles">
                    <div className="mpp-pack-order-id">{order.orderNumber}</div>
                    <div className="mpp-pack-order-sub">
                        {order.companySellerName || ''}
                        {order.companySellerName ? ' · ' : ''}
                        {total} items
                    </div>
                    <StatusBadge status={order.status} />
                </div>
                <div className="mpp-pack-checked-count">
                    <div className="mpp-pack-checked-num">{checked}/{total}</div>
                    <div className="mpp-pack-checked-label">checked</div>
                </div>
            </div>

            {order.expanded && (
                <div className="mpp-pack-checklist">
                    {order.items.map((item, idx) => (
                        <button
                            type="button"
                            key={item.productSKU + '-' + idx}
                            className={`mpp-pack-check-row ${item.isChecked ? 'mpp-pack-check-row--checked' : ''}`}
                            onClick={() => onToggleItem(order.id, idx)}
                        >
                            <span className={`mpp-pack-circle ${item.isChecked ? 'mpp-pack-circle--checked' : ''}`}>
                                {item.isChecked && <i className="fa fa-check" aria-hidden="true"></i>}
                            </span>
                            <span className="mpp-pack-item-main">
                                <span className="mpp-pack-item-name">{item.productName || item.productSKU}</span>
                                <span className="mpp-pack-item-sku">{item.productSKU}</span>
                            </span>
                            <span className="mpp-pack-item-qty">×{item.expectedQuantity}</span>
                        </button>
                    ))}
                </div>
            )}

            <div className="mpp-pack-actions">
                <button
                    type="button"
                    className="mpp-pack-toggle"
                    onClick={() => onToggleExpand(order.id)}
                >
                    <i
                        className={`fa ${order.expanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                        aria-hidden="true"
                    ></i>{' '}
                    {order.expanded ? 'Hide' : 'Check items'}
                </button>
                <button
                    type="button"
                    className={`mpp-pack-seal ${allChecked ? 'mpp-pack-seal--ready' : ''}`}
                    onClick={() => onSeal(order)}
                    disabled={isSealing}
                >
                    <i className="fa fa-print" aria-hidden="true"></i> Seal &amp; Print
                </button>
            </div>
        </div>
    );
};

PackOrderCard.propTypes = {
    order: PropTypes.object.isRequired,
    onToggleExpand: PropTypes.func.isRequired,
    onToggleItem: PropTypes.func.isRequired,
    onSeal: PropTypes.func.isRequired,
    sealingId: PropTypes.any,
};

const MobilePackPlan = () => {
    const dispatch = useDispatch();
    const { packingLabelListStatus, packingLabelList } = useSelector(
        (state) => state.pickerReducer
    );
    const [orders, setOrders] = useState([]); // both pending + packed
    const [activeTab, setActiveTab] = useState('pending');
    const [scanInput, setScanInput] = useState('');
    const [scanFeedback, setScanFeedback] = useState(null);
    const [bottomToast, setBottomToast] = useState(null);
    const [sealingId, setSealingId] = useState(null);
    const [listLoading, setListLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(
        moment().format('YYYY-MM-DDT00:00:00')
    );
    const scanInputRef = useRef(null);
    const scanTimerRef = useRef(null);
    const feedbackTimerRef = useRef(null);
    const toastTimerRef = useRef(null);

    useEffect(() => {
        scanInputRef.current?.focus();
        return () => {
            if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
            if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        };
    }, []);

    // Keep scan input always focused so the barcode scanner input is always
    // captured. Skip refocus when the user is interacting with another form
    // control (input/textarea/select/button) or inside a modal/dialog so
    // taps on cards, buttons, the date picker, and dialogs still work.
    useEffect(() => {
        const keepFocus = () => {
            const el = scanInputRef.current;
            if (!el) return;
            const active = document.activeElement;
            if (active === el) return;
            if (active && active !== document.body) {
                const tag = (active.tagName || '').toUpperCase();
                if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(tag)) return;
                if (typeof active.closest === 'function' && active.closest('.modal, [role="dialog"]')) return;
            }
            el.focus();
        };
        keepFocus();
        const interval = setInterval(keepFocus, 500);
        document.addEventListener('click', keepFocus);
        return () => {
            clearInterval(interval);
            document.removeEventListener('click', keepFocus);
        };
    }, []);

    // Fetch packing labels for the selected date (mirrors desktop PickList).
    const fetchPackingLabels = (dateValue) => {
        const ymd = moment(dateValue).format('YYYY-MM-DD');
        dispatch(
            getPackingLabels({
                pageIndex: 1,
                pageSize: 100,
                sortColumn: '',
                sortOrder: 'ASCENDING',
                searchkey: '',
                startDate: ymd,
                endDate: ymd,
            })
        );
    };

    useEffect(() => {
        fetchPackingLabels(selectedDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDate]);

    useEffect(() => {
        setListLoading(packingLabelListStatus === 'PENDING');
    }, [packingLabelListStatus]);

    // Map fetched packing labels into per-order cards. Cards from the list
    // start with empty items[]; items are lazy-loaded on expand or scan.
    useEffect(() => {
        const rows = packingLabelList?.packingLabelList || [];
        if (!rows.length) {
            setOrders((prev) => prev.filter((o) => o.fromScan));
            return;
        }
        const verifiedSet = new Set();
        rows.forEach((r) => {
            commaSeparatedStringToArray(r.verifiedOrderNumbers || '').forEach((n) => {
                if (n) verifiedSet.add(n.trim());
            });
        });
        const cards = [];
        rows.forEach((row) => {
            const orderNums = commaSeparatedStringToArray(row.orderNumber || '');
            orderNums.forEach((rawNum) => {
                const orderNumber = (rawNum || '').trim();
                if (!orderNumber) return;
                const isPacked = verifiedSet.has(orderNumber);
                cards.push({
                    id: `list-${orderNumber}`,
                    outboundId: null,
                    orderNumber,
                    trackingNumber: '',
                    companySellerName: row.companySellerName || '',
                    items: [],
                    status: isPacked ? STATUS.PACKED : STATUS.WAITING,
                    expanded: false,
                    scannedLabels: [],
                    fromScan: false,
                    itemsLoaded: false,
                });
            });
        });
        setOrders((prev) => {
            // Preserve any scan-driven cards (with outboundId / loaded items)
            // and overlay them on top of the list-driven cards.
            const scanned = prev.filter((o) => o.fromScan);
            const scannedNums = new Set(
                scanned.map((o) => (o.orderNumber || '').toLowerCase())
            );
            const merged = cards.filter(
                (c) => !scannedNums.has((c.orderNumber || '').toLowerCase())
            );
            return [...scanned, ...merged];
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [packingLabelList]);

    const showFeedback = (type, message, ms = 2500) => {
        setScanFeedback({ type, message });
        if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
        feedbackTimerRef.current = setTimeout(() => setScanFeedback(null), ms);
    };

    const showBottomToast = (message, ms = 2500) => {
        setBottomToast(message);
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        toastTimerRef.current = setTimeout(() => setBottomToast(null), ms);
    };

    const processScan = async (rawValue) => {
        const value = (rawValue || '').split(',')[0].trim();
        if (!value) return;
        setScanInput('');

        // De-duplicate: if order already in list (pending OR packed), just expand it.
        const existing = orders.find(
            (o) =>
                (o.orderNumber && o.orderNumber.toLowerCase() === value.toLowerCase()) ||
                (o.trackingNumber && o.trackingNumber.toLowerCase() === value.toLowerCase()) ||
                (o.scannedLabels || []).some((l) => l.toLowerCase() === value.toLowerCase())
        );
        if (existing) {
            setOrders((prev) =>
                prev.map((o) =>
                    o.id === existing.id
                        ? { ...o, expanded: true, scannedLabels: [...(o.scannedLabels || []), value] }
                        : o
                )
            );
            showFeedback('success', `Order ${existing.orderNumber} already loaded.`);
            scanInputRef.current?.focus();
            return;
        }

        showFeedback('success', `Looking up "${value}"…`);

        try {
            const res = await getOrderByLabelService({ orderNumber: value, companySellerId: 0 });
            const entity = res?.data?.Entity;
            const found = res?.data?.Status === 200 && Number(entity?.outboundId) > 0;
            if (!found) {
                showFeedback('error', `Order not found for "${value}".`);
                toast.error(`Order not found for "${value}".`);
                scanInputRef.current?.focus();
                return;
            }

            const newOrder = {
                id: entity.outboundId,
                outboundId: entity.outboundId,
                orderNumber: entity.orderNumber,
                trackingNumber: entity.trackingNumber || '',
                companySellerName: entity.companySellerName || entity.sellerName || '',
                items: [],
                status: STATUS.WAITING,
                expanded: true,
                scannedLabels: [value],
                fromScan: true,
                itemsLoaded: false,
            };
            // Replace any list-only placeholder card for the same order, then prepend.
            setOrders((prev) => {
                const filtered = prev.filter(
                    (o) =>
                        (o.orderNumber || '').toLowerCase() !==
                        (newOrder.orderNumber || '').toLowerCase()
                );
                return [newOrder, ...filtered];
            });
            showFeedback('success', `Loaded order ${newOrder.orderNumber}.`);

            // Fetch items for the checklist
            const itemsRes = await getOrderItemsService({
                outboundId: entity.outboundId,
                orderNumber: entity.orderNumber,
            });
            const rawItems = (itemsRes?.data?.Entity || itemsRes?.data?.EntityList || []) || [];
            const items = rawItems.map((it) => ({
                productSKU: it.productSKU || it.ProductSKU || '',
                productName: it.productName || it.ProductName || '',
                expectedQuantity: it.expectedQuantity || it.ExpectedQuantity || 0,
                isChecked: false,
            }));
            setOrders((prev) =>
                prev.map((o) =>
                    o.id === newOrder.id
                        ? {
                              ...o,
                              items,
                              itemsLoaded: true,
                              status: items.length ? STATUS.VALIDATING : STATUS.WAITING,
                          }
                        : o
                )
            );
        } catch (err) {
            console.error('MobilePackPlan: order lookup failed', err);
            showFeedback('error', `Lookup failed for "${value}".`);
            toast.error('Failed to look up order. Please retry.');
        } finally {
            scanInputRef.current?.focus();
        }
    };

    const toggleExpand = async (orderId) => {
        const target = orders.find((o) => o.id === orderId);
        if (!target) return;
        const willExpand = !target.expanded;
        setOrders((prev) =>
            prev.map((o) => (o.id === orderId ? { ...o, expanded: willExpand } : o))
        );
        // Lazy-load items the first time a list-driven card is expanded.
        if (
            willExpand &&
            !target.itemsLoaded &&
            target.status !== STATUS.PACKED &&
            target.orderNumber
        ) {
            try {
                const res = await getOrderByLabelService({
                    orderNumber: target.orderNumber,
                    companySellerId: 0,
                });
                const entity = res?.data?.Entity;
                if (!(res?.data?.Status === 200 && Number(entity?.outboundId) > 0)) {
                    toast.error(`Order details unavailable for ${target.orderNumber}.`);
                    return;
                }
                const itemsRes = await getOrderItemsService({
                    outboundId: entity.outboundId,
                    orderNumber: entity.orderNumber,
                });
                const rawItems =
                    (itemsRes?.data?.Entity || itemsRes?.data?.EntityList || []) || [];
                const items = rawItems.map((it) => ({
                    productSKU: it.productSKU || it.ProductSKU || '',
                    productName: it.productName || it.ProductName || '',
                    expectedQuantity: it.expectedQuantity || it.ExpectedQuantity || 0,
                    isChecked: false,
                }));
                setOrders((prev) =>
                    prev.map((o) =>
                        o.id === orderId
                            ? {
                                  ...o,
                                  outboundId: entity.outboundId,
                                  trackingNumber: entity.trackingNumber || o.trackingNumber,
                                  items,
                                  itemsLoaded: true,
                                  status: items.length
                                      ? STATUS.VALIDATING
                                      : STATUS.WAITING,
                              }
                            : o
                    )
                );
            } catch (err) {
                console.error('MobilePackPlan: lazy item load failed', err);
                toast.error('Failed to load order items. Please retry.');
            }
        }
    };

    const buildToggledOrder = (order, itemIdx) => {
        const items = order.items.map((it, i) =>
            i === itemIdx ? { ...it, isChecked: !it.isChecked } : it
        );
        const allChecked = items.length > 0 && items.every((it) => it.isChecked);
        let nextStatus = order.status;
        if (order.status !== STATUS.PACKED) {
            nextStatus = allChecked ? STATUS.COMPLETE : STATUS.VALIDATING;
        }
        return { ...order, items, status: nextStatus };
    };

    const toggleItem = (orderId, itemIdx) => {
        setOrders((prev) =>
            prev.map((o) => (o.id === orderId ? buildToggledOrder(o, itemIdx) : o))
        );
    };

    const handleSeal = async (order) => {
        const allChecked =
            order.items.length > 0 && order.items.every((it) => it.isChecked);
        if (!allChecked) {
            toast.warning('Tick all items before sealing the order.');
            return;
        }
        setSealingId(order.id);
        try {
            const res = await completePackVerificationService({
                outboundId: order.outboundId,
                orderNumber: order.orderNumber,
            });
            if (res?.data?.Status === 200) {
                setOrders((prev) =>
                    prev.map((o) =>
                        o.id === order.id
                            ? { ...o, status: STATUS.PACKED, expanded: false }
                            : o
                    )
                );
                showBottomToast('✓ Order sealed & label sent to printer');
            } else {
                toast.error(res?.data?.Message || 'Failed to seal order.');
            }
        } catch (err) {
            console.error('MobilePackPlan: seal failed', err);
            toast.error('Failed to seal order. Please retry.');
        } finally {
            setSealingId(null);
            scanInputRef.current?.focus();
        }
    };

    const pendingOrders = orders.filter((o) => o.status !== STATUS.PACKED);
    const packedOrders = orders.filter((o) => o.status === STATUS.PACKED);
    const visibleOrders = activeTab === 'pending' ? pendingOrders : packedOrders;
    const totalItemsCount = orders.reduce(
        (sum, o) => sum + o.items.reduce((s, it) => s + (it.expectedQuantity || 0), 0),
        0
    );

    return (
        <main className="mobile-pick-plan mobile-pack-plan">
            <div className="mpp-scan-wrapper">
                <div className="mpp-scan-box">
                    <i className="fa fa-th mpp-scan-icon" aria-hidden="true"></i>
                    <div className="mpp-scan-content">
                        <div className="mpp-scan-label">Scan Order ID or Tracking No.</div>
                        <input
                            ref={scanInputRef}
                            type="text"
                            className="mpp-scan-input"
                            value={scanInput}
                            onChange={(e) => {
                                const val = e.target.value;
                                setScanInput(val);
                                if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
                                if (val.trim()) {
                                    scanTimerRef.current = setTimeout(
                                        () => processScan(val),
                                        150
                                    );
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
                                    processScan(scanInput);
                                }
                            }}
                            placeholder="system detects automatically"
                            autoComplete="off"
                        />
                    </div>
                    <button
                        type="button"
                        className="mpp-scan-btn"
                        onClick={() => processScan(scanInput)}
                    >
                        SCAN
                    </button>
                </div>
            </div>

            {scanFeedback && (
                <div className={`pick-scan-feedback pick-scan-${scanFeedback.type} mpp-feedback`}>
                    <span className="pick-scan-icon">
                        {scanFeedback.type === 'success' && <i className="fa fa-check-circle"></i>}
                        {scanFeedback.type === 'error' && <i className="fa fa-times-circle"></i>}
                        {scanFeedback.type === 'warning' && (
                            <i className="fa fa-exclamation-triangle"></i>
                        )}
                    </span>
                    <span className="pick-scan-message">{scanFeedback.message}</span>
                </div>
            )}

            <div className="mpp-action-row mpp-pack-action-row">
                <span className="mpp-action-date">
                    <DatePickerComponent
                        dateField={selectedDate}
                        handleDateChange={(date) =>
                            setSelectedDate(moment(date).format('YYYY-MM-DDT00:00:00'))
                        }
                        fieldName="PackDate"
                        dateFormat="MM-dd-yyyy"
                        showFutureDate={false}
                    />
                </span>
            </div>

            <div className="mpp-stats">
                <div className="mpp-stat mpp-stat-pending">
                    <div className="mpp-stat-num">{pendingOrders.length}</div>
                    <div className="mpp-stat-label">PENDING</div>
                </div>
                <div className="mpp-stat mpp-stat-picked">
                    <div className="mpp-stat-num">{packedOrders.length}</div>
                    <div className="mpp-stat-label">PACKED</div>
                </div>
                <div className="mpp-stat mpp-stat-total">
                    <div className="mpp-stat-num">{totalItemsCount}</div>
                    <div className="mpp-stat-label">ITEMS</div>
                </div>
            </div>

            <div className="mpp-tabs">
                <button
                    type="button"
                    className={`mpp-tab ${activeTab === 'pending' ? 'active' : ''}`}
                    onClick={() => setActiveTab('pending')}
                >
                    Pending{' '}
                    <span className="mpp-tab-badge mpp-tab-badge-pending">
                        {pendingOrders.length}
                    </span>
                </button>
                <button
                    type="button"
                    className={`mpp-tab ${activeTab === 'packed' ? 'active' : ''}`}
                    onClick={() => setActiveTab('packed')}
                >
                    Packed{' '}
                    <span className="mpp-tab-badge mpp-tab-badge-picked">
                        {packedOrders.length}
                    </span>
                </button>
            </div>

            <div className="mpp-card-list">
                {(() => {
                    if (listLoading) {
                        return (
                            <div className="mpp-empty">
                                Loading orders for the selected date…
                            </div>
                        );
                    }
                    if (visibleOrders.length === 0) {
                        return (
                            <div className="mpp-empty">
                                {activeTab === 'pending'
                                    ? 'No pending orders for the selected date. Scan an Order ID or Tracking No. to add one.'
                                    : 'No packed orders for the selected date.'}
                            </div>
                        );
                    }
                    return (
                    <>
                        {visibleOrders.map((order) => (
                            <PackOrderCard
                                key={order.id}
                                order={order}
                                onToggleExpand={toggleExpand}
                                onToggleItem={toggleItem}
                                onSeal={handleSeal}
                                sealingId={sealingId}
                            />
                        ))}
                        <div className="mpp-end-of-list">
                            {activeTab === 'pending' ? '✓ No more records' : '✓ All orders packed'}
                        </div>
                    </>
                    );
                })()}
            </div>

            {bottomToast && (
                <output className="mpp-pack-toast" aria-live="polite">
                    {bottomToast}
                </output>
            )}
        </main>
    );
};

export default MobilePackPlan;
